import * as d3 from "d3";
import Config from "../config";
import { Deployment } from "../domain/deployment";
import { summaryKey } from "../domain/dimension";
import * as OrgFilter from "../domain/org-filter";
import { averageForDimension, Organization, OrgId, sortOrganizations, Year } from "../domain/organization";
import { catMaybes } from "../lib/array.extended";
import { clone } from "../lib/clone";
import { confidenceInterval } from "../lib/confidenceInterval";
import { memoize } from "../lib/memoize";
import { AtlasH, newAtlasH, RDone, Resource, RFailed, RLoading } from "./AtlasH";
import { Root } from "./Root";

export type SelectedYear =
  | { type: "Year"; year: Year } // Use this specific year
  | { type: "Development" }; // Show the development over multiple years.

export class DetailH {
  private atlasH: AtlasH;
  private onChanged: () => void;

  orgId: OrgId;
  state: {
    error: any;
    root: Resource<Root>;
    selectedYear: SelectedYear;
    selectedTab: string;
  };

  constructor(deployment: Deployment, locale: string, orgId: OrgId, selectedYear: SelectedYear, onChanged: () => void) {
    this.onChanged = onChanged;

    this.atlasH = newAtlasH(deployment, locale);
    this.orgId = orgId;
    this.state = {
      error: undefined,
      root: RLoading(),
      selectedYear,
      selectedTab: Config.yearSelectionEnabled ? "years" : "details",
    };
  }

  // Actions
  // --------------------------------------------------------------------------

  fetchData = (): void => {
    this.atlasH
      .fetchData(9999)
      .then(() => {
        if (this.atlasH.meta.type === "RDone") {
          if (this.atlasH.root) {
            const year = this.mostRecentYear;
            if (year) {
              // This is to force the 'orgResponses' computation so that it can be shared.
              this.atlasH.root.allOrganizations.forEach((org) => ((org.availableYears as any) + org.dimensions) as any);

              this.state.root = RDone(
                clone(this.atlasH.root, {
                  /*
                   * Force-disable region filter on the detail page. It causes issues in the german
                   * atlas where we default to ["DE"] and that means we'd have no data for organizations
                   * in the LE region.
                   *
                   * There is no way to change the regions on the detail page anyway.
                   */
                  regions: [],

                  year,
                }),
              );
              if (this.state.selectedYear.type === "Development") {
                this.setSelectedYear({ type: "Year", year });
              }
            } else {
              throw new Error("Unexpected error (DetailH fetchData/mostRecentYear)");
            }
          }
        } else {
          throw new Error("Unexpected error (DetailH fetchData/meta)");
        }
      })
      .catch((err) => {
        this.state = { ...this.state, error: "" + err };
      })
      .then(() => {
        this.onChanged();
      });
  };

  setSelectedTab = (selectedTab: string): void => {
    this.state = { ...this.state, selectedTab };
    this.onChanged();
  };

  setSelectedYear = (selectedYear: SelectedYear): void => {
    this.state = { ...this.state, selectedYear };
    if (selectedYear.type === "Year" && this.state.root.type === "RDone") {
      this.state = {
        ...this.state,
        root: RDone(clone(this.state.root.data, { year: selectedYear.year })),
      };
    }
    this.onChanged();
  };

  resetSelectedYear = (): void => {
    const mostRecentYear = this.mostRecentYear;
    if (mostRecentYear) {
      this.setSelectedYear({ type: "Year", year: mostRecentYear });
    }
  };

  // Queries
  // --------------------------------------------------------------------------

  get error() {
    return this.state.error;
  }

  get loading() {
    return this.atlasH.isLoading;
  }

  get selectedTab() {
    return this.state.selectedTab;
  }

  get organizationResource(): Resource<Organization> {
    return this._organizationResource(this.state.root, this.orgId);
  }
  private _organizationResource = memoize((rootResource: Resource<Root>, orgId: OrgId): Resource<Organization> => {
    if (rootResource.type === "RDone") {
      const org = rootResource.data.orgById(orgId);
      if (org) {
        return RDone(clone(org, { globalConfidenceInterval: () => this.confidenceInterval }));
      } else {
        return RFailed("Organization not found");
      }
    } else {
      return rootResource;
    }
  });

  get mostRecentYear(): undefined | Year {
    return this.availableYears[this.availableYears.length - 1];
  }

  get selectedYear(): SelectedYear {
    return this.state.selectedYear;
  }

  get availableYears(): Year[] {
    return this._availableYears(this.atlasH.root, this.orgId);
  }
  _availableYears = memoize((root: undefined | Root, orgId: OrgId): Year[] => {
    if (root) {
      const org = root.orgById(orgId);
      if (org) {
        return org.availableYears;
      }
    }
    return [];
  });

  // All organizations in the same industry as the one we're focusing on.
  get industryOrganizations(): Organization[] {
    const orgRes = this.organizationResource;
    const selectedYear = this.selectedYear;
    if (orgRes.type === "RDone" && selectedYear.type === "Year") {
      const industryFilter = OrgFilter.toggle(OrgFilter.empty, "industry", orgRes.data.industry_object.key);
      const industryOrgs = OrgFilter.filterOrgs(industryFilter, this.organizations).filter(
        (o) => o.availableYears.indexOf(selectedYear.year) !== -1,
      );

      return sortOrganizations(industryOrgs, summaryKey);
    } else {
      return [];
    }
  }

  get organizations(): Organization[] {
    return this._organizations(this.state.root);
  }
  _organizations = memoize((rootResource: Resource<Root>): Organization[] => {
    if (rootResource.type === "RDone") {
      return rootResource.data.visibleOrganizations.map((x) =>
        clone(x, {
          globalConfidenceInterval: () => this.confidenceInterval,
        }),
      );
    } else {
      return [];
    }
  });

  get confidenceInterval(): [number, number] {
    return this._confidenceInterval(this.organizations);
  }
  _confidenceInterval = memoize((organizations: Organization[]) => {
    const values = catMaybes(organizations.map((org) => averageForDimension(org, summaryKey)));
    return confidenceInterval(values, d3.mean(values));
  });
}

export const newDetailH = (
  deployment: Deployment,
  locale: string,
  orgId: OrgId,
  selectedYear: SelectedYear,
  onChanged: () => void,
) => new DetailH(deployment, locale, orgId, selectedYear, onChanged);
